// This file stored the column definition used by Ag-Grid
// The definitions here are tightly coupled with the data model on the backend.
// This tight coupling is generally undesirable (as changes to the backend require
// changes here).

import { enums } from "../../../data";

const role = { ADMIN: "ADMIN", FACULTY: "FACULTY", STUDENT: "STUDENT" };

const sortEnums = (item1, item2, theEnum) => {
  return theEnum.indexOf(item1) - theEnum.indexOf(item2);
};

export function getColumns(userRole) {
  let columns = [
    {
      field: "row",
      headerName: "Row",
      checkboxSelection: true,
      rowDrag: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      pinned: window.innerWidth > 450 ? "left" : "",
      width: 110,
      filter: "agNumberColumnFilter",
      editable: false,
      comparator: (valueA, valueB, nodeA, nodeB, isInverted) =>
        Number(valueA) - Number(valueB),
    },
    // Personal Info
    {
      headerName: "Personal Information",
      groupId: "PersonalGroup",
      children: [
        {
          field: "firstName",
          headerName: "First Name",
          pinned: window.innerWidth > 600 ? "left" : "",
          width: 100,
          filter: "agTextColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "textCellEditor",
        },
        {
          field: "lastName",
          headerName: "Last Name",
          pinned: window.innerWidth > 600 ? "left" : "",
          width: 100,
          filter: "agTextColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "textCellEditor",
        },
        {
          field: "preferredName",
          headerName: "Preferred Name",
          pinned: window.innerWidth > 600 ? "left" : "",
          width: 100,
          filter: "agTextColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "textCellEditor",
          columnGroupShow: "open",
        },
        {
          field: "jhed",
          headerName: "JHED",
          pinned: window.innerWidth > 800 ? "left" : "",
          width: 100,
          columnGroupShow: "open",
          filter: "agTextColumnFilter",
        },
        {
          field: "email",
          headerName: "Email",
          cellRenderer: "emailFormatter",
          width: 150,
          pinned: window.innerWidth > 800 ? "left" : "",
          columnGroupShow: "open",
          filter: "agTextColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "longTextCellEditor",
          cellEditorParams: {
            maxLength: 1000,
            rows: 3,
            cols: 70,
          },
        },
      ],
    },
    // Application Info
    {
      headerName: "Application",
      groupId: "ApplicationGroup",
      children: [
        {
          field: "status",
          headerName: "Status",
          width: 100,
          headerTooltip: "Application Status",
          filter: "selectFilter",
          filterParams: {
            values: enums.applicationStatus,
          },
          comparator: (valueA, valueB) =>
            sortEnums(valueA, valueB, enums.applicationStatus),
          editable: userRole === role.ADMIN,
          cellEditor: "selectCellEditor",
          cellEditorParams: {
            values: ["", ...enums.applicationStatus],
          },
        },
        {
          field: "createdAt",
          headerName: "Created At",
          cellRenderer: "dateFormatter",
          width: 200,
          columnGroupShow: "open",
          filter: "agDateColumnFilter",
          filterParams: {
            comparator: function (filterLocalDateAtMidnight, cellValue) {
              if (cellValue === null) return -1;
              let cellDate = new Date(cellValue);
              if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
                return 0;
              }
              if (cellDate < filterLocalDateAtMidnight) {
                return -1;
              }
              if (cellDate > filterLocalDateAtMidnight) {
                return 1;
              }
            },
            browserDatePicker: true,
          },
          editable: false,
        },
        {
          field: "year",
          headerName: "Year",
          hide: true,
          width: 80,
          columnGroupShow: "open",
          filter: "agNumberColumnFilter",
          editable: false,
        },
        {
          field: "term",
          headerName: "Term",
          hide: true,
          width: 80,
          columnGroupShow: "open",
          filter: "selectFilter",
          filterParams: {
            values: enums.term,
          },
          comparator: (valueA, valueB) => sortEnums(valueA, valueB, enums.term),
          editable: false,
        },
      ],
    },
    // Admin
    {
      headerName: "Admin",
      groupId: "AdminGroup",
      children: [
        {
          field: "evaluation",
          headerName: "Evaluation",
          width: 150,
          columnGroupShow: "open",
          headerTooltip: "Last CA Evaluation",
          cellClass: "ag-admin-col",
          filter: "selectFilter",
          filterParams: {
            values: enums.evaluation,
          },
          comparator: (valueA, valueB) =>
            sortEnums(valueA, valueB, enums.evaluation),
          editable: userRole === role.ADMIN,
          cellEditor: "selectCellEditor",
          cellEditorParams: {
            values: ["", ...enums.evaluation],
          },
        },
        {
          field: "adminFeedback",
          headerName: "Feedback",
          width: 150,
          headerTooltip: "Admin Feedback",
          cellClass: "ag-admin-col",
          filter: "selectFilter",
          filterParams: {
            values: enums.adminFeedback,
          },
          comparator: (valueA, valueB) =>
            sortEnums(valueA, valueB, enums.adminFeedback),
          editable: userRole === role.ADMIN,
          cellEditor: "selectCellEditor",
          cellEditorParams: {
            values: ["", ...enums.adminFeedback],
          },
        },
        {
          field: "adminNotes",
          headerName: "Note",
          cellRenderer: "notePopover",
          width: 80,
          columnGroupShow: "open",
          headerTooltip: "Admin Note",
          cellClass: "ag-admin-col",
          filter: "agTextColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "noteCellEditor",
          cellEditorParams: {
            maxLength: 1000,
            rows: 15,
            cols: 70,
          },
        },
        {
          field: "hiredFor",
          headerName: "Hired for",
          columnGroupShow: "open",
          width: 200,
          cellClass: "ag-admin-col",
          filter: "agTextColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "textCellEditor",
        },
      ],
    },
    // Academics
    {
      headerName: "Academics",
      groupId: "AcademicsGroup",
      children: [
        {
          field: "gradePointAverage",
          headerName: "GPA",
          width: 80,
          filter: "agNumberColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "numericCellEditor",
          cellEditorParams: {
            min: "0",
            max: "4",
            step: "0.1",
          },
        },
        {
          field: "program",
          headerName: "Program",
          columnGroupShow: "open",
          filter: "agTextColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "textCellEditor",
        },
        {
          field: "classYear",
          headerName: "Class Year",
          width: 120,
          columnGroupShow: "open",
          filter: "selectFilter",
          filterParams: {
            values: enums.classYear,
          },
          comparator: (valueA, valueB) =>
            sortEnums(valueA, valueB, enums.classYear),
          editable: userRole === role.ADMIN,
          cellEditor: "selectCellEditor",
          cellEditorParams: {
            values: ["", ...enums.classYear],
          },
        },
        {
          field: "transcript",
          headerName: "Transcript",
          cellRenderer: "linkMask",
          width: 100,
          columnGroupShow: "open",
          editable: userRole === role.ADMIN,
          cellEditor: "longTextCellEditor",
          cellEditorParams: {
            maxLength: 1000,
            rows: 15,
            cols: 70,
          },
        },
      ],
    },
    // Experience
    {
      headerName: "Experience",
      groupId: "ExperienceGroup",
      children: [
        {
          field: "experience",
          headerName: "Teaching",
          width: 120,
          headerTooltip: "Teaching Experience",
          filter: "selectFilter",
          filterParams: {
            values: enums.teachingExperience,
          },
          comparator: (valueA, valueB) =>
            sortEnums(valueA, valueB, enums.teachingExperience),
          editable: userRole === role.ADMIN,
          cellEditor: "selectCellEditor",
          cellEditorParams: {
            values: ["", ...enums.teachingExperience],
          },
        },
        {
          field: "resume",
          headerName: "Resume",
          cellRenderer: "linkMask",
          width: 100,
          columnGroupShow: "open",
          editable: userRole === role.ADMIN,
          cellEditor: "longTextCellEditor",
          cellEditorParams: {
            maxLength: 1000,
            rows: 3,
            cols: 70,
          },
        },
        {
          field: "linkedin",
          headerName: "LinkedIn",
          cellRenderer: "linkMask",
          width: 100,
          columnGroupShow: "open",
          editable: userRole === role.ADMIN,
          cellEditor: "longTextCellEditor",
          cellEditorParams: {
            maxLength: 1000,
            rows: 3,
            cols: 70,
          },
        },
        {
          field: "comment",
          headerName: "Other",
          cellRenderer: "notePopover",
          width: 80,
          columnGroupShow: "open",
          filter: "agTextColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "longTextCellEditor",
          cellEditorParams: {
            maxLength: 1000,
            rows: 15,
            cols: 70,
          },
        },
      ],
    },
    // Availability
    {
      headerName: "Availability",
      groupId: "AvailabilityGroup",
      children: [
        {
          field: "availableHours",
          headerName: "Available Hours",
          width: 100,
          headerTooltip: "Available Hours",
          filter: "agNumberColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "numericCellEditor",
          cellEditorParams: {
            min: "1",
            max: "19",
            step: "1",
          },
        },
        {
          field: "creditHours",
          headerName: "Credit Hours",
          width: 100,
          headerTooltip: "Credit Hours",
          columnGroupShow: "open",
          filter: "agNumberColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "numericCellEditor",
          cellEditorParams: {
            min: "0",
            max: "40",
            step: "1",
          },
        },
        {
          field: "otherJobHours",
          headerName: "Other Jobs Hours",
          width: 100,
          headerTooltip: "Other Jobs Hours",
          columnGroupShow: "open",
          filter: "agNumberColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "numericCellEditor",
          cellEditorParams: {
            min: "0",
            max: "19",
            step: "1",
          },
        },
      ],
    },
    // HR Info
    {
      headerName: "HR Info",
      groupId: "HrGroup",
      children: [
        {
          field: "enrollmentStatus",
          headerName: "Enrollment Status",
          width: 120,
          filter: "selectFilter",
          filterParams: {
            values: enums.enrollmentStatus,
          },
          comparator: (valueA, valueB) =>
            sortEnums(valueA, valueB, enums.enrollmentStatus),
          editable: userRole === role.ADMIN,
          cellEditor: "selectCellEditor",
          cellEditorParams: {
            values: ["", ...enums.enrollmentStatus],
          },
        },
        {
          field: "residency",
          headerName: "Residency",
          width: 120,
          columnGroupShow: "open",
          filter: "selectFilter",
          filterParams: {
            values: enums.residency,
          },
          comparator: (valueA, valueB) =>
            sortEnums(valueA, valueB, enums.residency),
          editable: userRole === role.ADMIN,
          cellEditor: "selectCellEditor",
          cellEditorParams: {
            values: ["", ...enums.residency],
          },
        },
        {
          field: "workStudy",
          headerName: "Work-Study",
          width: 80,
          columnGroupShow: "open",
          filter: "selectFilter",
          filterParams: {
            values: enums.yesNo,
          },
          comparator: (valueA, valueB) =>
            sortEnums(valueA, valueB, enums.yesNo),
          editable: userRole === role.ADMIN,
          cellEditor: "selectCellEditor",
          cellEditorParams: {
            values: ["", ...enums.yesNo],
          },
        },
        {
          field: "payroll",
          headerName: "Payroll",
          width: 120,
          columnGroupShow: "open",
          filter: "selectFilter",
          filterParams: {
            values: ["", ...enums.payroll],
          },
          comparator: (valueA, valueB) =>
            sortEnums(valueA, valueB, enums.payroll),
          editable: userRole === role.ADMIN,
          cellEditor: "selectCellEditor",
          cellEditorParams: {
            values: enums.payroll,
          },
        },
      ],
    },
    // Referee
    {
      headerName: "Reference",
      groupId: "ReferenceGroup",
      children: [
        {
          field: "refereeName",
          headerName: "Referee Name",
          width: 150,
          filter: "agTextColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "textCellEditor",
        },
        {
          field: "refereeEmail",
          headerName: "Referee Email",
          cellRenderer: "emailFormatter",
          width: 120,
          columnGroupShow: "open",
          filter: "agTextColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "longTextCellEditor",
          cellEditorParams: {
            maxLength: 1000,
            rows: 3,
            cols: 70,
          },
        },
      ],
    },
  ];

  const getGetOrdinal = (cardinal) => {
    const suffix = ["th", "st", "nd", "rd"];
    const value = cardinal % 100;
    return value + (suffix[(value - 20) % 10] || suffix[value] || suffix[0]);
  };

  const choice = [
    "",
    "First Choice",
    "Second Choice",
    "Third Choice",
    "Fourth Choice",
  ];
  for (let c = 1; c <= 4; c++) {
    columns.push({
      headerName: choice[c],
      groupId: `CourseSelectionGroup${c}`,
      children: [
        {
          field: `course${c}`,
          headerName: `Course (${getGetOrdinal(c)})`,
          width: 200,
          cellClass: c % 2 === 0 ? "ag-light-col" : "ag-dark-col",
          filter: "agTextColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "textCellEditor",
        },
        {
          field: `grade${c}`,
          headerName: `Grade (${getGetOrdinal(c)})`,
          width: 80,
          columnGroupShow: "open",
          cellClass: c % 2 === 0 ? "ag-light-col" : "ag-dark-col",
          filter: "selectFilter",
          filterParams: {
            values: enums.grades,
          },
          comparator: (valueA, valueB) =>
            sortEnums(valueA, valueB, enums.grades),
          editable: userRole === role.ADMIN,
          cellEditor: "selectCellEditor",
          cellEditorParams: {
            values: ["", ...enums.grades],
          },
        },
        {
          field: `experience${c}`,
          headerName: `Experience (${getGetOrdinal(c)})`,
          width: 120,
          columnGroupShow: "open",
          cellClass: c % 2 === 0 ? "ag-light-col" : "ag-dark-col",
          filter: "selectFilter",
          filterParams: {
            values: enums.courseExperience,
          },
          comparator: (valueA, valueB) =>
            sortEnums(valueA, valueB, enums.courseExperience),
          editable: userRole === role.ADMIN,
          cellEditor: "selectCellEditor",
          cellEditorParams: {
            values: ["", ...enums.courseExperience],
          },
        },
        {
          field: `head${c}`,
          headerName: `Head? (${getGetOrdinal(c)})`,
          width: 80,
          columnGroupShow: "open",
          cellClass: c % 2 === 0 ? "ag-light-col" : "ag-dark-col",
          filter: "selectFilter",
          filterParams: {
            values: enums.yesNo,
          },
          comparator: (valueA, valueB) =>
            sortEnums(valueA, valueB, enums.yesNo),
          editable: userRole === role.ADMIN,
          cellEditor: "selectCellEditor",
          cellEditorParams: {
            values: ["", ...enums.yesNo],
          },
        },
        {
          field: `comment${c}`,
          headerName: `Student Comment (${getGetOrdinal(c)})`,
          cellRenderer: "notePopover",
          width: 150,
          columnGroupShow: "open",
          cellClass: c % 2 === 0 ? "ag-light-col" : "ag-dark-col",
          filter: "agTextColumnFilter",
          editable: userRole === role.ADMIN,
          cellEditor: "longTextCellEditor",
          cellEditorParams: {
            maxLength: 1000,
            rows: 15,
            cols: 70,
          },
        },
        {
          field: `instructorFeedback${c}`,
          headerName: `Faculty Feedback (${getGetOrdinal(c)})`,
          width: 150,
          columnGroupShow: "open",
          cellClass: c % 2 === 0 ? "ag-light-col" : "ag-dark-col",
          filter: "selectFilter",
          filterParams: {
            values: enums.instructorFeedback,
          },
          comparator: (valueA, valueB) =>
            sortEnums(valueA, valueB, enums.instructorFeedback),
          editable: userRole === role.ADMIN || userRole === role.FACULTY,
          cellEditor: "selectCellEditor",
          cellEditorParams: {
            values: ["", ...enums.instructorFeedback],
          },
        },
        {
          field: `instructorComment${c}`,
          headerName: `Faculty Note (${getGetOrdinal(c)})`,
          cellRenderer: "notePopover",
          width: 150,
          columnGroupShow: "open",
          cellClass: c % 2 === 0 ? "ag-light-col" : "ag-dark-col",
          filter: "agTextColumnFilter",
          editable: userRole === role.ADMIN || userRole === role.FACULTY,
          cellEditor: "noteCellEditor",
          cellEditorParams: {
            maxLength: 1000,
            rows: 15,
            cols: 70,
          },
        },
      ],
    });
  }

  return columns;
}
